import type {
  CreateSkillTagBody,
  GetSkillTagsQuery,
  GetSkillTagsResult,
  SkillTag,
  UpdateSkillTagBody,
} from '@didaskill/domain/skill-tags/types';

import { HttpRepository } from '../httpRepository';

const e = encodeURIComponent;

const SKILL_TAGS_API_URL = '/api/v1/school/skill-tags';

export class SkillTagsRepository extends HttpRepository {
  /**
   * Get a list of skill tags.
   *
   * The caller declares through `TSkillTag` the shape it asked for: pass
   * {@link SkillTagWithSkillsCount} along with `includeSkillsCount`.
   *
   * @param limit - Pagination limit.
   * @param offset - Pagination offset.
   * @param q - Search text matched against the skill tag label.
   * @param includeSkillsCount - Whether each tag carries its number of skills.
   * @returns List of skill tags and total number of skill tags matching the filters.
   */
  async getSkillTags<TSkillTag extends SkillTag = SkillTag>({
    limit,
    offset,
    q,
    includeSkillsCount,
  }: GetSkillTagsQuery = {}): Promise<GetSkillTagsResult<TSkillTag>> {
    const { body } = await this.http
      .get(SKILL_TAGS_API_URL)
      .query({ limit, offset, q, includeSkillsCount });

    return body;
  }

  /**
   * Create a new skill tag.
   *
   * @param body - Skill tag data.
   * @returns Created skill tag.
   */
  async createSkillTag(body: CreateSkillTagBody): Promise<SkillTag> {
    const { body: createdSkillTag } = await this.http.post(SKILL_TAGS_API_URL).send(body);

    return createdSkillTag;
  }

  /**
   * Update an existing skill tag.
   *
   * @param skillTagId - Skill tag id.
   * @param body - Skill tag data to update.
   * @returns Updated skill tag.
   */
  async updateSkillTag(skillTagId: string, body: UpdateSkillTagBody): Promise<SkillTag> {
    const { body: updatedSkillTag } = await this.http
      .patch(`${SKILL_TAGS_API_URL}/${e(skillTagId)}`)
      .send(body);

    return updatedSkillTag;
  }

  /**
   * Delete an existing skill tag.
   *
   * @param skillTagId - Skill tag id.
   * @returns Void.
   */
  async deleteSkillTag(skillTagId: string): Promise<void> {
    await this.http.delete(`${SKILL_TAGS_API_URL}/${e(skillTagId)}`);
  }
}
